import { ITEM_IDS } from '@runkr/config'
import type { ItemBalance } from '@runkr/config'
import type { EventSink } from '../events'
import { bodyOverlaps, centerX } from '../geom'
import { nextInt } from '../rng'
import type { Rng } from '../rng'
import { chargeRide } from '../player/rides'
import { NO_TICK } from '../world'
import type { ItemId, World } from '../world'

/**
 * Crate roll (D-022): the weight row for `rank` (clamped to the table), one `nextInt(total)`,
 * then walk the row in `ITEM_IDS` order.
 */
export function rollItem(rank: number, rng: Rng, items: ItemBalance): ItemId {
  const rows = items.weightsByRank
  const row = rows[Math.min(Math.max(rank, 1), rows.length) - 1]!
  let total = 0
  for (const w of row) total += w
  let r = nextInt(rng, total)
  for (let k = 0; k < row.length; k++) {
    r -= row[k]!
    if (r < 0) return ITEM_IDS[k]!
  }
  throw new Error('rollItem: weight row does not cover the roll')
}

/**
 * Step 6 (crates and coins), by player index. A racing player overlapping an available crate
 * with an empty slot gets `rollItem(rank)` and the crate goes away for `crateRespawnTicks`; a
 * player already holding an item leaves the crate where it is (D-002). Two players on one crate
 * in one tick: the lower index takes it. Coins count once per match.
 */
export function stepPickups(world: World, events: EventSink): void {
  for (let i = 0; i < world.players.length; i++) {
    const p = world.players[i]!
    if (p.placement !== 0 || p.respawnAtTick !== NO_TICK) continue
    takeCrates(world, i, events)
    takeCoins(world, i, events)
  }
}

function takeCrates(world: World, i: number, events: EventSink): void {
  const p = world.players[i]!
  const { tick } = world
  const { crateSizePx: size, crateRespawnTicks } = world.cfg.items
  for (const c of world.crates) {
    if (p.item !== null) return
    if (tick < c.respawnAtTick || !reaches(world, i, c.x, c.y, size)) continue
    p.item = rollItem(p.rank, world.rng.crates, world.cfg.items)
    c.respawnAtTick = tick + crateRespawnTicks
    events.push({ type: 'pickup', tick, player: i, crate: c.id, item: p.item })
  }
}

function takeCoins(world: World, i: number, events: EventSink): void {
  const p = world.players[i]!
  const size = world.cfg.items.coinSizePx
  const { coins } = world.track
  const base = i * coins.length
  for (let k = 0; k < coins.length; k++) {
    const c = coins[k]!
    if (world.coinsTaken[base + k] || !reaches(world, i, c.x, c.y, size)) continue
    world.coinsTaken[base + k] = true
    p.coins++
    chargeRide(world, p)
    events.push({ type: 'coin', tick: world.tick, player: i, coin: k })
  }
}

/**
 * Player `i` picks up the `size` box at (`x`, `y`): by touching it, or with an active Magnet when
 * the box centre is within `radiusPx` of the runner's centre on both axes (T3.12).
 */
function reaches(world: World, i: number, x: number, y: number, size: number): boolean {
  const p = world.players[i]!
  if (bodyOverlaps(p, { x, y, w: size, h: size })) return true
  if (p.magnetUntil <= world.tick) return false
  const r = world.cfg.items.magnet.radiusPx
  const half = size / 2
  return Math.abs(x + half - centerX(p)) <= r && Math.abs(y + half - (p.y - p.h / 2)) <= r
}
